<template>
  <el-table class="docutax-submission-table" ref="docutaxSubmissionsTable" stripe :data="filteredRequests"
    style="width: 100%" :default-sort="{ prop: 'createdDate', order: 'descending' }">
    <el-table-column label="Status" property="status" width="100" sortable>
      <template slot-scope="{ row }">
        <docutax-submission-status-tag-edit :saving="isUpdatingStatusMap[row.id]" class="editable-tag" :value="row.status"
          @update:value="$event => onUpdateStatus(row, $event)" />
      </template>
    </el-table-column>
    <el-table-column property="officeId" label="Office" width="90" sortable>
      <template slot-scope="{ row }">
        {{ getEFINForOffice(row.officeId) }}
        <docutax-office-assignment-edit v-if="isDistrictManager" :saving="isUpdatingOffice[row.id]" :value="row.officeId"
          @update:value="$event => onTransferOffice(row, $event)" />
      </template>
    </el-table-column>
    <el-table-column property="assignedPreparerName" width="170" label="Assigned Preparer" sortable>
      <template slot-scope="{ row }">
        {{ row.assignedPreparerName }}
        <el-button v-loading="isUpdatingPreprarerAssignment[row.id]" :disabled="isUpdatingPreprarerAssignment[row.id]"
          size="small" type="text" v-show="row.assignedPreparerName == null" @click="onAssignToMe(row)">Assign to
          me</el-button>
        <el-button v-loading="isUpdatingPreprarerAssignment[row.id]" :disabled="isUpdatingPreprarerAssignment[row.id]"
          size="small" type="text" v-show="row.assignedPreparerName != null && row.assignedPreparerId === user.id"
          @click="onUnassign(row)">Unassign</el-button>
      </template>
    </el-table-column>
    <el-table-column property="clientName" label="Client Name" sortable>
      <template slot-scope="{ row }">
        {{ row.clientName }}
        <br
          v-show="row.clientCellPhone != null && row.clientCellPhone.length && row.clientEmailAddress != null && row.clientEmailAddress.length" />
        <small>{{ row.clientCellPhone }}</small>
        <br
          v-show="row.clientCellPhone != null && row.clientCellPhone.length && row.clientEmailAddress != null && row.clientEmailAddress.length" />
        <small>{{ row.clientEmailAddress }}</small>
        <br />
        <el-link icon="el-icon-s-promotion" type="primary" @click="onSendRequest(row)">Send New Request</el-link>
      </template>
    </el-table-column>
    <el-table-column property="documents" label="Documents" min-width="110">
      <template slot-scope="{ row }">
        <el-row class="documents-column" type="flex" align="top" style="flex-direction: column; gap: 12px;">
          <small v-show="row.files.length === 0"><i>No documents</i></small>
          <el-button v-show="row.getTotalHeicAndImageUrlCount() > 0" type="text" @click="viewImages(row)">
            {{ row.getTotalHeicAndImageUrlCount() }} image(s)
          </el-button>
          <el-link type="primary" :href="pdfUrl" target="_blank" v-for="(pdfUrl, index) in row.getPDFUrls()"
            :key="index"><i class="fa fa-file-pdf" />
            {{ getFileName(pdfUrl) }}
          </el-link>
          <el-button v-show="row.getUnsupportedFileTypes().length > 0" disabled type="text">
            {{ row.getUnsupportedFileTypes().length }} unsupported file type(s)
          </el-button>
        </el-row>
      </template>
    </el-table-column>

    <el-table-column property="message" label="Message" min-width="120">
      <template slot-scope="{ row }">
        <template v-if="!row.originatingRequestId">
          {{ row.message }}
        </template>
        <template v-else>
          <small>Client Response</small><br />
          {{ row.message }}
          <small class="text-italic" v-show="!row.message">No message included</small>
          <br /><br />
          <el-popover placement="bottom" width="400" trigger="click">
            <el-link style="font-size: 0.8em" type="primary" slot="reference">View Preparer Request</el-link>
            <small>Preparer Request<br /></small>
            {{ row.originatingRequestMessage }}<br />
            <small class="text-italic">Sent by {{ row.originatingRequestPreparerName }} on {{
              formatDate(row.originatingRequestCreatedDate)
            }}
            </small>
          </el-popover>
        </template>
      </template>
    </el-table-column>
    <el-table-column property="createdDate" label="Created" width="110" sortable>
      <template slot-scope="{ row }">{{ formatDate(row.createdDate) }}</template>
    </el-table-column>
  </el-table>
</template>

<script>
import 'viewerjs/dist/viewer.css';
import VueViewer from 'v-viewer';
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import heic2any from 'heic2any';
import DocutaxSubmissionStatusTagEdit from '@/components/tags/DocutaxSubmissionStatusEdit.vue';
import DocutaxOfficeAssignmentEdit from '@/components/form-item/DocutaxOfficeAssignmentEdit.vue';
import FileUrlUtils from '@/util/FileUrlUtils';
import DocutaxSubmissionSearchResult from '@/model/DocutaxSubmissionSearchResult';


import api from '@/api';

Vue.use(VueViewer);

export default {
  name: 'DocutaxSubmissionsTable',
  components: {
    DocutaxOfficeAssignmentEdit,
    DocutaxSubmissionStatusTagEdit,
  },
  props: {
    submissions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isUpdatingStatusMap: {}, // row ids => bool
      isUpdatingPreprarerAssignment: {}, // row ids => bool
      isUpdatingOffice: {}, // row ids => bool
    };
  },
  methods: {
    ...mapActions('Lookup', ['getOffices']),
    formatDate(date) {
      return moment(date).format('L LT');
    },
    getEFINForOffice(id) {
      return (this.officeMap[id] ? this.officeMap[id].efin : '');
    },
    handleModifyClick({ method, row }) {
      method(row);
    },
    getFileName: FileUrlUtils.getFileName,
    async viewImages(row) {
      const imageUrls = row.getImageUrls();
      const allImageUrls = [...imageUrls];

      const heicUrls = row.getHeicUrls();
      if (heicUrls.length > 0) {
        const loading = this.$loading({
          lock: true,
          text: 'Converting images...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)',
        });
        try {
          const heicConversions = await this.convertAllHeicImages(heicUrls);
          heicConversions.forEach(convertedImage => allImageUrls.push(URL.createObjectURL(convertedImage)));
        } finally {
          loading.close();
        }
      }

      this.$viewerApi({
        images: allImageUrls,
        options: {
          movable: false,
          zIndex: 1999,
          toolbar: {
            reset: false,
            prev: true,
            next: true,
            zoomIn: true,
            zoomOut: true,
            oneToOne: true,
            rotateLeft: true,
            rotateRight: true,
            flipVertical: true,
            flipHorizontal: true,
          },
        },
      });
    },
    async convertAllHeicImages(heicUrls) {
      const promises = heicUrls.map(url => new Promise((resolve, reject) => {
        fetch(url, {
        })
          .then((response) => {
            if (!response.ok) {
              console.error('Network request to load HEIC failed', response);
              throw new Error('Network response was not OK');
            }
            return response.blob();
          })
          .then(blob => heic2any({ blob }))
          .then((conversionResultBlob) => {
            resolve(conversionResultBlob);
          })
          .catch((e) => {
            reject(e);
          });
      }));

      const results = await Promise.allSettled(promises);

      const failures = results.filter(result => result.status === 'rejected');
      if (failures.length > 0) {
        console.error('Failed to convert some images', failures);
        this.$message.error(`${failures.length} images could not be loaded`);
      }

      return results.filter(result => result.status === 'fulfilled').map(result => result.value);
    },
    async onUpdateStatus(row, newStatus) {
      if (!row.id) {
        return;
      }

      try {
        this.$set(this.isUpdatingStatusMap, row.id, true);

        await api.docutaxSubmissions.applyStatus(row.id, newStatus);

        const clonedRow = new DocutaxSubmissionSearchResult(row);
        clonedRow.status = newStatus;

        this.$emit('rowUpdated', clonedRow);
      } catch {
        this.$message.error(`Failed to apply status to row id ${row.id}`);
      } finally {
        this.$set(this.isUpdatingStatusMap, row.id, false);
      }
    },
    async onAssignToMe(row) {
      if (!row.id) {
        return;
      }

      try {
        this.$set(this.isUpdatingPreprarerAssignment, row.id, true);

        await api.docutaxSubmissions.assignToMe(row.id);

        const clonedRow = new DocutaxSubmissionSearchResult(row);
        clonedRow.assignedPreparerId = this.user.id;
        clonedRow.assignedPreparerName = this.user.fullName;

        this.$emit('rowUpdated', clonedRow);
      } catch {
        this.$message.error(`Failed to assign preparer to row id ${row.id}`);
      } finally {
        this.$set(this.isUpdatingPreprarerAssignment, row.id, false);
      }
    },
    async onUnassign(row) {
      if (!row.id) {
        return;
      }

      try {
        this.$set(this.isUpdatingPreprarerAssignment, row.id, true);

        await api.docutaxSubmissions.removeAssignment(row.id);

        const clonedRow = new DocutaxSubmissionSearchResult(row);
        clonedRow.assignedPreparerId = undefined;
        clonedRow.assignedPreparerName = undefined;

        this.$emit('rowUpdated', clonedRow);
      } catch {
        this.$message.error(`Failed to remove preparer assignment from row id ${row.id}`);
      } finally {
        this.$set(this.isUpdatingPreprarerAssignment, row.id, false);
      }
    },
    async onTransferOffice(row, newOfficeId) {
      if (!row.id || !newOfficeId) {
        return;
      }

      try {
        this.$set(this.isUpdatingOffice, row.id, true);

        await api.docutaxSubmissions.transferOffice(row.id, newOfficeId);

        const clonedRow = new DocutaxSubmissionSearchResult(row);
        clonedRow.officeId = newOfficeId;

        this.$emit('rowUpdated', clonedRow);
      } catch {
        this.$message.error(`Failed to apply office transfer to row id ${row.id}`);
      } finally {
        this.$set(this.isUpdatingOffice, row.id, false);
      }
    },
    onSendRequest(row) {
      this.$emit('sendRequest', row);
    },
  },
  computed: {
    ...mapGetters('User', [
      'isDistrictManager',
      'isManager',
      'user',
    ]),
    ...mapGetters('Lookup', [
      'officeMap',
    ]),
    filteredRequests() {
      return this.submissions;
    },
  },
  mounted() {
    this.getOffices();
  },
};

</script>

<style lang="scss" scoped>
.client-header {
  h2 {
    margin: 0;
    padding: 0 5px;
    font-size: 1.8em;

    span {
      font-weight: normal;
      color: #999;
      font-size: 0.75em;
      text-transform: uppercase;
    }
  }
}

.editable-tag {
  cursor: pointer;
}

.documents-column {
  .el-button {
    padding-bottom: 0;
  }

  .el-button:disabled {
    cursor: default;
  }

  .el-button+.el-button {
    margin-left: 0;
  }
}
</style>
